import {defineStore} from 'pinia';
import {isCustomColor} from "../../helpers/common";

export const usePortalContentAndDesignStore = defineStore('portalContentAndDesignStore', {
    state: () => ({
        // Shared
        portalInitialized: false,
        initialPortalPageData: null,
        liveDesignerEnabled: false,
        program: {},
        portalLayoutSettings: {},
        translationSettings: {},
        translationSettingsConfig: [],
        availableTranslations: [],
        selectedPredefinedLanguage: '',
        partneroBrandingSettings: {},
        srcRoutes: {},
        recaptchaSettings: false,
        countryOptions: [],
        isPreview: false,

        // Auth-specific
        isAuthView: false,
        authUser: {},
        avatarDetails: null,
        showOnboarding: false,
        onboardingStep: null,
        userMenu: {},
        menu: {},
        dashboardPageSettings: {},
        referralsPageSettings: {},
        referralPageSettings: {},
        resourcesPageSettings: {},
        payoutsPageSettings: {},
        reportsPageSettings: {},
        profilePageSettings: {},
        invitationPageSettings: {},
        approvalPageSettings: {},
        invitationKey: null,
        appDiskUrl: null,

        // Public-only
        signup: false,
        loginPageSettings: {},
        landingPageSettings: {},
        leadsPageSettings: {},
        signupPageSettings: {},
        resetPasswordPageSettings: {},

        // Forms
        signupFormData: {
            name: '',
            last_name: '',
            username: '',
            email: '',
            password: '',
            tos: false,
            privacy_policy: false,
            marketing_consent: false,
            key: '',
            cfields: {},
        },
        signupFormErrors: {
            responseError: null,
            validationErrors: null,
        },
        leadsFormData: {
            name: '',
            username: '',
            submitter_name: null,
            submitter_email: null,
            cfields: {},
        },
        leadsFormErrors: {
            responseError: null,
            validationErrors: null,
        },
        leadsPartnerLoginFormData: {
            email: null,
            password: null,
            otp_token: null,
            otp_identifier: null,
            otp: {
                step: 1
            }
        },
        approvalFormData: {
            cfields: {},
        },
        approvalFormErrors: {
            responseError: null,
            validationErrors: null,
        },
        loginFormData: {
            email: '',
            password: '',
            otpIdentifier: '',
            otpIniSuccessMessage: '',
            token: '',
        },
        loginFormErrors: {
            responseError: null,
            validationErrors: null
        },
        loggingInProgress: false
    }),

    actions: {
        initializePortalContent(data, isAuthView = false, isPreview = false) {
            this.isAuthView = isAuthView;
            this.isPreview = isPreview;
            this.portalInitialized = true;
            this.initialPortalPageData = JSON.parse(JSON.stringify(data));
            this.liveDesignerEnabled = data.live_designer_enabled || false;
            this.program = data.program || {};
            this.portalLayoutSettings = data.portal_layout_settings || {};
            this.translationSettings = data.translation_settings || {};
            this.translationSettingsConfig = data.translation_settings_config || {};
            this.availableTranslations = data.available_translations || [];
            this.partneroBrandingSettings = data.partnero_branding_settings || {};
            this.srcRoutes = data.src_routes || {};
            this.countryOptions = data.country_options || [];
            this.recaptchaSettings = data.recaptcha_settings || null;
            this.appDiskUrl = data.app_disk_url || null;

            if (isAuthView) {
                this.authUser = data.auth_user || {};
                this.avatarDetails = data.avatar_details || null;
                this.showOnboarding = data.show_onboarding || false;
                this.onboardingStep = data.onboarding_step || null;
                this.userMenu = data.user_menu || {};
                this.menu = data.menu || {};
                this.invitationKey = data.invitation_key || null;
            } else {
                this.leadsPageSettings = data.leads_page_settings || {};
                this.loginPageSettings = data.login_page_settings || {};
                this.landingPageSettings = data.landing_page_settings || {};
                this.signupPageSettings = data.signup_page_settings || {};
                this.resetPasswordPageSettings = data.reset_password_page_settings || {};
                this.populateSignupFormCustomFields();
                this.populateLeadsFormCustomFields();
            }
        },

        initializePortalPageContent(page, id = null) {
            let url;
            if (page === 'referral' && id) {
                url = route('partners_int.page_settings.referral', {id});
            } else {
                url = route(`partners_int.page_settings.${page}`);
            }

            return axios.get(url)
                .then(response => {
                    if (page === 'dashboard') {
                        this.dashboardPageSettings = response.data || {};
                        window.growthChart = response.data.growth_chart || null;
                    }
                    if (page === 'referrals') {
                        this.referralsPageSettings = response.data || {};
                    }
                    if (page === 'referral') {
                        this.referralPageSettings = response.data || {};
                    }
                    if (page === 'leads') {
                        this.leadsPageSettings = response.data || {};
                    }
                    if (page === 'resources') {
                        this.resourcesPageSettings = response.data || {};
                    }
                    if (page === 'payouts') {
                        this.payoutsPageSettings = response.data || {};
                    }
                    if (page === 'reports') {
                        this.reportsPageSettings = response.data || {};
                    }
                    if (page === 'affiliate-profile') {
                        this.profilePageSettings = response.data || {};
                    }
                    if (page === 'invitation') {
                        this.invitationPageSettings = response.data || {};
                    }
                    if (page === 'approval') {
                        this.approvalPageSettings = response.data || {};
                        this.populateApprovalFormCustomFields()
                    }
                })
                .catch(error => {
                    console.error('Error initializing portal content:', error);
                });
        },

        resetCurrentPageToInitialState() {
            if (this.initialPortalPageData) {
                this.initializePortalContent(this.initialPortalPageData, this.isAuthView);
            }
        },

        populateSignupFormCustomFields() {
            const signupCustomFields = this.signupPageSettings?.signup_form?.fields || [];
            signupCustomFields.forEach((field) => {
                if (field?.id) {
                    this.signupFormData.cfields[field.id] = field.type === 'checkbox' ? {} : '';
                }
            });
        },

        populateLeadsFormCustomFields() {
            const leadsCustomFields = this.leadsPageSettings?.leads_form?.fields || [];
            leadsCustomFields.forEach((field) => {
                if (field?.id) {
                    this.leadsFormData.cfields[field.id] = field.type === 'checkbox' ? {} : '';
                }
            });
        },

        populateApprovalFormCustomFields() {
            const approvalCustomFields = this.approvalPageSettings?.approval_form?.fields || [];
            approvalCustomFields.forEach((field) => {
                if (field?.id) {
                    this.approvalFormData.cfields[field.id] = field.type === 'checkbox' ? {} : '';
                }
            });
        },

        resetFormErrors(formName) {
            if (formName === 'signup') {
                this.signupFormErrors.responseError = null;
                this.signupFormErrors.validationErrors = null;
            } else if (formName === 'leads') {
                this.leadsFormErrors.responseError = null;
                this.leadsFormErrors.validationErrors = null;
            } else if (formName === 'login') {
                this.loginFormErrors.responseError = null;
                this.loginFormErrors.validationErrors = null;
            } else if (formName === 'approval') {
                this.approvalFormErrors.responseError = null;
                this.approvalFormErrors.validationErrors = null;
            }
        },

        resetLeadsFormAndErrorData() {
            this.leadsFormData.name = '';
            this.leadsFormData.username = '';
            this.resetFormErrors('leads');
            this.populateLeadsFormCustomFields();
        },

        getAppFileSrc(path, defaultValue = null) {
            if (path && this.appDiskUrl) {
                return this.appDiskUrl.replace(/\/+$/, '') + '/' + path.replace(/^\/+/, '');
            }
            return defaultValue;
        }
    },

    getters: {
        // Routes
        getFileUploadRoute(state) {
            const routeName = state.liveDesignerEnabled
                ? 'partners_int.live-designer.file.upload'
                : 'app_int.program_settings_file.upload';
            return route(routeName, {program: state.program.id});
        },
        getFileDeleteRoute(state) {
            const routeName = state.liveDesignerEnabled
                ? 'partners_int.live-designer.file.delete'
                : 'app_int.program_settings_file.delete';
            return route(routeName, {program: state.program.id});
        },
        getFileUploadNoImageRoute() {
            return "/images/upload-image.png"
        },
        getFileResourcesUploadRoute(state) {
            return route('partners_int.live-designer.resources.file.upload', {program: state.program.id});
        },
        getFileResourcesDeleteRoute(state) {
            return route('partners_int.live-designer.resources.file.delete', {program: state.program.id});
        },
        getDashboardFileUploadRoute(state) {
            return route('app_int.program.portal.dashboard.resources.file_upload', {program: state.program.id})
        },
        getDashboardFileDeleteRoute(state) {
            return route('app_int.program.portal.dashboard.resources.file_delete', {program: state.program.id})
        },
        getResourcePageFileUploadRoute(state) {
            return route('app_int.program.portal.resources.file_upload', {program: state.program.id})
        },
        getResourcePageFileDeleteRoute(state) {
            return route('app_int.program.portal.resources.file_delete', {program: state.program.id})
        },
        getLeadsRoute() {
            return route('partners.leads.show');
        },
        getLoginRoute() {
            return route('partners.login.show');
        },
        getRegisterRoute() {
            return route('partners.register.show');
        },
        getForgotPasswordRoute() {
            return route('partners.password.request.show');
        },
        getSocialAuthLoginRedirectRoute() {
            return (provider) => route('partners.affiliate.social-auth.user.login.redirect', {provider: provider});
        },
        getSocialAuthRegisterRedirectRoute() {
            return (provider) => route('partners.affiliate.social-auth.user.register.redirect', {provider: provider});
        },
        getHomeRoute() {
            return route('partners.dashboard');
        },
        getPartnerLogoutRoute() {
            return route('partners.logout.show');
        },
        getPayoutRequestRoute() {
            return route('partners_int.payouts.request');
        },
        getPayoutSetPayoutInfoRoute(state) {
            return route('partners_int.payouts.save-payout-details', {program: state.program.id});
        },
        getRefreshBankRequirementsRoute() {
            return route('partners_int.payouts.bank_transfer.refresh-requirements');
        },
        getPayoutCancelRoute() {
            return route('partners_int.payouts.cancel')
        },
        getPayoutResubmitRoute() {
            return route('partners_int.payouts.resubmit')
        },
        getReportingDataRoute() {
            return route('partners_int.views.reporting.referrals_analytics')
        },
        getProfileAvatarUploadRoute() {
            return route('partners_int.profile.avatar.upload')
        },
        getProfileAvatarDeleteRoute() {
            return route('partners_int.profile.avatar.delete')
        },
        getDeleteAccountRoute() {
            return route('partners_int.delete_account')
        },


        // Page Settings
        getLandingPageSettings(state) {
            return state.landingPageSettings;
        },


        // Other
        getCountryOptions: (state) => state.countryOptions,

        // Public pages Colors & Styling
        getPageContentTextColorClass(state) {
            return isCustomColor(state.portalLayoutSettings.public_page_content_text_color)
                ? 'text-custom-content-text'
                : `text-${state.portalLayoutSettings.public_page_content_text_color}`;
        },
        getLinkColorClass(state) {
            return isCustomColor(state.portalLayoutSettings.public_page_content_link_color)
                ? 'text-custom-link'
                : `text-${state.portalLayoutSettings.public_page_content_link_color}`;
        },
        getPageBackgroundColorClass(state) {
            return isCustomColor(state.portalLayoutSettings.public_page_background_color)
                ? 'bg-custom-bg'
                : `bg-${state.portalLayoutSettings.public_page_background_color}`;
        },
        getPageBackgroundGradientColorClass(state) {
            if (state.portalLayoutSettings.public_page_background_gradient_color === 'none') {
                return '';
            }
            return `bg-gradient-to-b from-${
                isCustomColor(state.portalLayoutSettings.public_page_background_gradient_color)
                    ? "custom-gradient-bg"
                    : state.portalLayoutSettings.public_page_background_gradient_color
            }`
        },
        getPageContentBackgroundColorClass(state) {
            return isCustomColor(state.portalLayoutSettings.public_page_content_background_color)
                ? 'bg-content-bg'
                : `bg-${state.portalLayoutSettings.public_page_content_background_color}`;
        },
        getPageContentButtonColorClass(state) {
            return isCustomColor(state.portalLayoutSettings.public_page_content_button_color)
                ? 'custom-content'
                : state.portalLayoutSettings.public_page_content_button_color;
        },
        getPageContentLayoutCornerClass(state) {
            return state.portalLayoutSettings.referral_portal_content_layout_corners || 'rounded-xl';
        },
        getInputStyle(state) {
            const defaultStyle = 'transition-shadow block w-full rounded focus:ring-0 py-2.5 px-3.5 text-sm border-gray-300 focus:border-gray-400';

            if (state.portalLayoutSettings.public_page_content_input_style === 'white') {
                return [defaultStyle, 'bg-white text-gray-900'].join(' ');
            } else {
                return [defaultStyle, `bg-transparent ${this.getPageContentTextColorClass}`].join(' ');
            }
        },
        getCheckboxInputStyle(state) {
            const defaultStyle = 'h-5 w-5 rounded cursor-pointer focus:ring-0 focus:ring-offset-0 border-gray-300 focus:border-gray-400';
            if (state.portalLayoutSettings.public_page_content_input_style === 'white') {
                return [defaultStyle, 'bg-white text-gray-900'].join(' ');
            } else {
                return [defaultStyle, `bg-transparent ${this.getPageContentTextColorClass}`].join(' ');
            }
        },
        getPageContainerClasses(state) {
            const layout = state.portalLayoutSettings.public_page_content_layout;
            const sidebar = state.portalLayoutSettings.portal_signup_sidebar_side;
            const sidebarWidth = state.portalLayoutSettings.portal_signup_sidebar_width;
            const image = state.srcRoutes.public_page_background_image;
            const corners = state.portalLayoutSettings.public_page_content_layout_corners;
            const style = state.portalLayoutSettings.public_page_content_layout_style;

            const classes = [];

            if (layout === "one") {
                classes.push(isCustomColor(state.portalLayoutSettings.public_page_background_color)
                    ? "bg-custom-bg"
                    : `bg-${state.portalLayoutSettings.public_page_background_color}`);
                classes.push("rounded");
                if (image && sidebar === "none") classes.push("px-10 py-10");
            } else if (["two", "three"].includes(layout)) {
                classes.push(isCustomColor(state.portalLayoutSettings.public_page_content_background_color)
                    ? "bg-content-bg"
                    : `bg-${state.portalLayoutSettings.public_page_content_background_color}`);
                if (layout === "two") {
                    if (style !== "none") classes.push(style);
                    if (corners !== "none") classes.push(corners);
                }
            }

            if (layout !== "one") {
                if (sidebarWidth === "lg:w-8/12") classes.push("px-14 py-20");
                else if (sidebarWidth === "lg:w-6/12") classes.push("px-12 py-14");
                else classes.push("px-10 py-10");
            }

            return classes.join(" ");
        },

        // Auth-pages colors & styling
        getPortalLayoutBackgroundColor(state) {
            return isCustomColor(state.portalLayoutSettings.portal_layout_background_color)
                ? 'bg-custom-bg'
                : `bg-${state.portalLayoutSettings.portal_layout_background_color}`;
        },
        getNavBackgroundColorClass(state) {
            return isCustomColor(state.portalLayoutSettings.portal_layout_brand_primary_color)
                ? 'bg-custom-bg'
                : `bg-${state.portalLayoutSettings.portal_layout_brand_primary_color}`;
        },
        getBorderNavigationColorClass(state) {
            if (state.portalLayoutSettings.portal_layout_navigation_color === 'white') {
                return 'border-b border-white border-opacity-10'
            } else if (state.portalLayoutSettings.portal_layout_navigation_color === 'black') {
                return 'border-b border-black border-opacity-10'
            } else if (state.portalLayoutSettings.portal_layout_navigation_color === 'gray') {
                return 'border-b border-gray-200 border-gray-75'
            } else {
                return 'border-b border-white border-opacity-10'
            }
        },
        getTextColorClass(state) {
            return isCustomColor(state.portalLayoutSettings.portal_layout_text_color)
                ? 'text-custom-text'
                : `text-${state.portalLayoutSettings.portal_layout_text_color}`;
        },
        getTextPrimaryColorClass(state) {
            return isCustomColor(state.portalLayoutSettings.portal_layout_brand_primary_color)
                ? 'text-custom-primary'
                : `text-${state.portalLayoutSettings.portal_layout_brand_primary_color}`;
        },
        getBackgroundPrimaryColorClass(state) {
            return isCustomColor(state.portalLayoutSettings.portal_layout_brand_primary_color)
                ? 'bg-custom-primary'
                : `bg-${state.portalLayoutSettings.portal_layout_brand_primary_color}`;
        },
        getBorderPrimaryColorClass(state) {
            return isCustomColor(state.portalLayoutSettings.portal_layout_brand_primary_color)
                ? 'border-custom-primary'
                : `border-${state.portalLayoutSettings.portal_layout_brand_primary_color}`;
        },
        getBorderTextColorClass(state) {
            return isCustomColor(state.portalLayoutSettings.portal_layout_text_color)
                ? 'border-custom-text'
                : `border-${state.portalLayoutSettings.portal_layout_text_color}`;
        },
        getPrimaryColorRaw(state) {
            return isCustomColor(state.portalLayoutSettings.portal_layout_brand_primary_color)
                ? 'custom-primary'
                : state.portalLayoutSettings.portal_layout_brand_primary_color;
        },
        getPortalLayoutButtonColorClass(state) {
            return isCustomColor(state.portalLayoutSettings.portal_layout_button_color)
                ? 'custom'
                : state.portalLayoutSettings.portal_layout_button_color;
        },
    }
});
